import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ApiError } from '@/api/http'
import { authApi, type LoginPayload, type RegisterPayload } from '@/api/auth.api'
import { type CurrentUser } from '@/types/domain'

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous'

/**
 * Authentication state. The backend (cookie auth) is authoritative — this store only reflects
 * session state and never makes authorization decisions. `fetchSession` is safe to call on
 * bootstrap; `register`/`login`/`logout` hit /api/auth/* and update local state from the response.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const status = ref<AuthStatus>('unknown')

  const isAuthenticated = computed(() => status.value === 'authenticated')

  function setUser(value: CurrentUser): void {
    user.value = value
    status.value = 'authenticated'
  }

  async function fetchSession(): Promise<void> {
    try {
      const me = await authApi.me()
      setUser(me)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        clear()
        return
      }
      // Transient/unavailable — treat as anonymous so guarded routes redirect to /login.
      clear()
    }
  }

  async function register(payload: RegisterPayload): Promise<CurrentUser> {
    const me = await authApi.register(payload)
    setUser(me)
    return me
  }

  async function login(payload: LoginPayload): Promise<CurrentUser> {
    const me = await authApi.login(payload)
    setUser(me)
    return me
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // Best-effort: even if the network call fails, drop local session state below.
    } finally {
      clear()
    }
  }

  function clear(): void {
    user.value = null
    status.value = 'anonymous'
  }

  return { user, status, isAuthenticated, fetchSession, register, login, logout, clear }
})
